import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  Wallet as WalletIcon,
  Lock,
  Inbox,
  Coins,
  AlertTriangle,
  Landmark,
  Bike,
  Store,
  Percent,
  Users,
  Building2,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Search,
  Receipt,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { PageGuard } from '@components/PageGuard';
import { StatCard } from '@components/dashboard/StatCard';
import {
  moneyDashboardService,
  normalizeSettled,
  type MoneyBucket,
  type MoneyCompositionRow,
  type SettledBucketRow,
} from '@services/moneyDashboard.service';
import { HELD_INCOMING_NOTE, RAW_AVAILABLE_NOTE } from '@services/ledger.service';
import { cn, formatCurrency } from '@lib/utils';
import { humanizeCode, labelTxnType } from './ledgerFormat';

const PAGE_SIZE = 25;

const inputClass =
  'rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-3 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors';

const thClass =
  'text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] whitespace-nowrap';

const tdClass = 'px-6 py-4 text-[14px] text-[var(--text-primary)] whitespace-nowrap';

const sectionCard = 'rounded-3xl bg-[var(--bg-surface)] shadow-soft overflow-hidden';

// One colour per bucket, fixed so the donut slice and the card agree. Six
// chart tokens cover seven buckets, so `platform` — the smallest and least
// operationally interesting — is a blend of two tokens rather than a repeat.
const BUCKET_COLOR: Record<MoneyBucket, string> = {
  escrow: 'var(--chart-1)',
  merchant: 'var(--chart-2)',
  commission: 'var(--chart-3)',
  driver: 'var(--chart-4)',
  tax: 'var(--chart-5)',
  customer: 'var(--chart-6)',
  platform: 'color-mix(in srgb, var(--chart-6) 60%, var(--chart-3))',
};

const BUCKET_ICON: Record<MoneyBucket, typeof WalletIcon> = {
  escrow: Lock,
  merchant: Store,
  commission: Percent,
  driver: Bike,
  tax: Landmark,
  customer: Users,
  platform: Building2,
};

/** Plain English for what the bucket actually is — an operator should not have
 *  to know which wallet types roll into it. */
const BUCKET_BLURB: Record<MoneyBucket, string> = {
  escrow: 'Paid in, not yet confirmed. Belongs to nobody until the buyer confirms.',
  merchant: 'Merchant earnings — business and branch wallets, held plus withdrawable.',
  commission: 'Chekit commission on goods, plus the margin taken on delivery.',
  driver: 'Rider earnings from completed deliveries.',
  tax: 'VAT/TOT set aside for remittance. Zero while merchants remit their own.',
  customer: 'Customer personal, savings and rewards wallets.',
  platform: 'Chekit operating, promotion and ad-revenue wallets.',
};

function bucketColor(bucket: MoneyBucket): string {
  return BUCKET_COLOR[bucket] ?? 'var(--chart-6)';
}

/** Stranded holds are the only figure on this page that warrants alarm.
 *  A negative raw `available` is normal in-flight escrow — see `heldSemantics`. */
function strandedClass(value: number): string {
  return value > 0
    ? 'text-[var(--state-danger)] font-semibold'
    : 'text-[var(--text-muted)] font-medium';
}

const microLabel =
  'text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]';

/** `YYYY-MM-DD` for a date `days` before today — the default settled window. */
function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

interface DonutDatum {
  name: string;
  value: number;
  bucket: MoneyBucket;
  share: string;
}

const DonutTooltip = ({ active, payload }: { active?: boolean; payload?: unknown[] }) => {
  if (!active || !payload || payload.length === 0) return null;
  const datum = (payload[0] as { payload: DonutDatum }).payload;
  return (
    <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-3 text-[13px] shadow-lift">
      <p className="font-semibold text-[var(--text-primary)]">{datum.name}</p>
      <p className="mt-1 font-medium text-[var(--text-accent)]">
        {formatCurrency(datum.value)}
      </p>
      <p className="text-[var(--text-muted)]">{datum.share}% of platform balance</p>
    </div>
  );
};

export const MoneyDashboardPage = () => {
  const [from, setFrom] = useState(() => isoDaysAgo(30));
  const [to, setTo] = useState(() => isoDaysAgo(0));

  const [merchantSearch, setMerchantSearch] = useState('');
  const [merchantQ, setMerchantQ] = useState('');
  const [merchantPage, setMerchantPage] = useState(1);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['money-dashboard', 'composition', from, to],
    queryFn: () => moneyDashboardService.getDashboard({ from, to }),
    placeholderData: keepPreviousData,
  });

  const {
    data: merchants,
    isLoading: merchantsLoading,
    error: merchantsError,
    isFetching: merchantsFetching,
  } = useQuery({
    queryKey: ['money-dashboard', 'merchants', merchantPage, merchantQ],
    queryFn: () =>
      moneyDashboardService.listMerchants({
        page: merchantPage,
        size: PAGE_SIZE,
        q: merchantQ || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const composition: MoneyCompositionRow[] = data?.composition ?? [];
  const totals = data?.totals;
  const flags = data?.flags ?? [];

  // A flag is only useful next to the number it describes. Bucket-level flags
  // are the ones rendered on the card; wallet-type flags would repeat the same
  // sentence per code inside the bucket.
  const flagsByBucket = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const f of flags) {
      if (f.level && f.level !== 'bucket') continue;
      map.set(f.bucket, [...(map.get(f.bucket) ?? []), f.message]);
    }
    return map;
  }, [flags]);

  const settled: SettledBucketRow[] = useMemo(
    () => normalizeSettled(data?.settled),
    [data?.settled],
  );

  // A pie cannot draw a negative slice. Buckets sitting at or below zero are
  // named underneath the chart instead of being silently dropped.
  const positiveBuckets = composition.filter((c) => c.balance > 0);
  const excludedBuckets = composition.filter((c) => c.balance <= 0);
  const donutTotal = positiveBuckets.reduce((sum, c) => sum + c.balance, 0);

  const donutData: DonutDatum[] = positiveBuckets.map((c) => ({
    name: c.label || humanizeCode(c.bucket),
    value: c.balance,
    bucket: c.bucket,
    share: donutTotal > 0 ? ((c.balance / donutTotal) * 100).toFixed(1) : '0.0',
  }));

  const merchantRows = merchants?.data ?? [];
  const merchantTotal = merchants?.total ?? 0;
  const merchantLastPage = Math.max(1, Math.ceil(merchantTotal / PAGE_SIZE));

  return (
    <PageGuard permission="wallets.read">
      <div className="space-y-8">
        {/* Header */}
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            Finance
          </p>
          <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
            Money dashboard
          </h1>
          <p className="text-[14px] text-[var(--text-muted)]">
            Every shilling on the platform, sorted by whose it is: tax, driver,
            merchant, commission, still in escrow, customer, Chekit's own.
          </p>
        </div>

        {/* Platform totals */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          <StatCard
            label="Balance"
            value={totals ? formatCurrency(totals.balance) : '—'}
            icon={WalletIcon}
            variant="green"
            footer="What is actually sitting in wallets right now"
          />
          <StatCard
            label="Incoming (earmarked)"
            value={totals ? formatCurrency(totals.heldIncoming) : '—'}
            icon={Inbox}
            variant="purple"
            footer={HELD_INCOMING_NOTE}
          />
          <StatCard
            label="Stranded"
            value={totals ? formatCurrency(totals.heldStranded) : '—'}
            icon={AlertTriangle}
            variant={totals && totals.heldStranded > 0 ? 'red' : 'green'}
            footer={
              totals && totals.heldStranded > 0
                ? 'Held against baskets that already finished — clear from the treasury adjust-hold worklist'
                : 'No holds sitting on finished baskets'
            }
          />
          <StatCard
            label="Spendable"
            value={totals ? formatCurrency(totals.spendable) : '—'}
            icon={Coins}
            variant="purple"
            footer="Balance minus holds that genuinely lock it"
          />
        </div>

        {/* The DB's own figures — kept auditable, deliberately demoted. */}
        {totals && (
          <p className="text-[12px] text-[var(--text-muted)]">
            <Lock className="mr-1 inline h-3 w-3 align-[-1px]" strokeWidth={1.5} />
            {RAW_AVAILABLE_NOTE}:{' '}
            <span className="font-medium text-[var(--text-secondary)]">
              {formatCurrency(totals.available)}
            </span>{' '}
            · held {formatCurrency(totals.held)}.{' '}
            {data?.heldSemantics?.rawAvailable ??
              "Escrow records each party's share as a hold on the DESTINATION wallet while the cash stays in chekit_escrow, so the earmark is subtracted from a balance that never contained it. A negative here is normal while orders are in flight."}
          </p>
        )}

        {/* Flags — only stranded holds. A negative available is NOT a fault. */}
        {flags.length > 0 && (
          <div className="rounded-3xl border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] p-6 space-y-3">
            <div className="flex items-center gap-2">
              <AlertTriangle
                className="h-5 w-5 shrink-0 text-[var(--state-danger)]"
                strokeWidth={1.5}
              />
              <h2 className="text-[15px] font-semibold text-[var(--state-danger)]">
                {flags.length}{' '}
                {flags.length === 1 ? 'stranded hold needs' : 'stranded holds need'}{' '}
                clearing
              </h2>
            </div>
            <ul className="space-y-1.5">
              {flags.map((f, i) => (
                <li key={`${f.bucket}-${f.walletTypeCode ?? 'bucket'}-${i}`} className="text-[13px] text-[var(--text-primary)]">
                  <span className="font-semibold capitalize">{f.bucket}</span>
                  {f.walletTypeCode && (
                    <span className="text-[var(--text-muted)]"> · {f.walletTypeCode}</span>
                  )}{' '}
                  — {f.message}
                </li>
              ))}
            </ul>
            <Link
              to="/finance/wallets"
              className="inline-flex items-center gap-2 rounded-full border border-[var(--state-danger-border)] px-4 py-2 text-[13px] font-medium text-[var(--state-danger)] transition-colors hover:border-[var(--state-danger)]"
            >
              Open the wallets to clear them
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
            </Link>
          </div>
        )}

        {/* Composition */}
        <section className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="space-y-1">
              <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                Composition
              </h2>
              <p className="text-[13px] text-[var(--text-muted)]">
                Open a bucket to see the individual wallets that make it up.
              </p>
            </div>
            {isFetching && !isLoading && (
              <span className="text-[13px] text-[var(--text-muted)]">Updating...</span>
            )}
          </div>

          {isLoading && (
            <div className={cn(sectionCard, 'p-12 text-center text-[14px] text-[var(--text-muted)]')}>
              Loading money composition...
            </div>
          )}

          {!!error && !isLoading && (
            <div className={cn(sectionCard, 'p-12 text-center text-[14px] text-[var(--state-danger)]')}>
              Failed to load the money dashboard.
            </div>
          )}

          {!isLoading && !error && composition.length === 0 && (
            <div className={cn(sectionCard, 'p-12 text-center text-[14px] text-[var(--text-muted)]')}>
              No wallet buckets returned.
            </div>
          )}

          {composition.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {composition.map((c) => {
                const Icon = BUCKET_ICON[c.bucket] ?? WalletIcon;
                const bucketFlags = flagsByBucket.get(c.bucket) ?? [];
                // Only stranded holds are alarming. A negative raw `available`
                // is what a normal in-flight escrow earmark looks like.
                const alarming = c.heldStranded > 0;
                const primaryCode = c.walletTypeCodes?.[0];

                return (
                  <Link
                    key={c.bucket}
                    to={
                      primaryCode
                        ? `/finance/wallets?walletTypeCode=${primaryCode}`
                        : '/finance/wallets'
                    }
                    className={cn(
                      'block rounded-3xl bg-[var(--bg-surface)] p-6 shadow-soft transition-all duration-300',
                      'hover:-translate-y-0.5 hover:shadow-lift',
                      alarming && 'border border-[var(--state-danger-border)]',
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className="h-8 w-1.5 shrink-0 rounded-full"
                          style={{ backgroundColor: bucketColor(c.bucket) }}
                        />
                        <div className="min-w-0">
                          <div className="text-[15px] font-semibold text-[var(--text-primary)] truncate">
                            {c.label || humanizeCode(c.bucket)}
                          </div>
                          <div className="text-[12px] text-[var(--text-muted)]">
                            {c.walletCount} {c.walletCount === 1 ? 'wallet' : 'wallets'}
                          </div>
                        </div>
                      </div>
                      <Icon
                        className={cn(
                          'h-5 w-5 shrink-0',
                          alarming ? 'text-[var(--state-danger)]' : 'text-[var(--text-muted)]',
                        )}
                        strokeWidth={1.5}
                      />
                    </div>

                    <div className="mt-4 font-display text-[28px] font-medium leading-none tracking-[-0.03em] text-[var(--text-primary)]">
                      {formatCurrency(c.balance)}
                    </div>

                    <div className="mt-4 grid grid-cols-3 gap-3 border-t border-[var(--border-subtle)] pt-4">
                      <div>
                        <div className={microLabel} title={HELD_INCOMING_NOTE}>
                          Incoming
                        </div>
                        <div className="text-[14px] font-medium text-[var(--text-secondary)]">
                          {formatCurrency(c.heldIncoming)}
                        </div>
                      </div>
                      <div>
                        <div className={microLabel}>Stranded</div>
                        <div
                          className={cn(
                            'text-[14px] flex items-center gap-1.5',
                            strandedClass(c.heldStranded),
                          )}
                        >
                          {alarming && (
                            <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} />
                          )}
                          {formatCurrency(c.heldStranded)}
                        </div>
                      </div>
                      <div>
                        <div className={microLabel}>Spendable</div>
                        <div className="text-[14px] font-medium text-[var(--text-primary)]">
                          {formatCurrency(c.spendable)}
                        </div>
                      </div>
                    </div>

                    {/* DB value stays on screen — demoted, never hidden. */}
                    <p className="mt-3 text-[11px] text-[var(--text-muted)]">
                      {RAW_AVAILABLE_NOTE}: {formatCurrency(c.available)} · held{' '}
                      {formatCurrency(c.held)}
                    </p>

                    {bucketFlags.length > 0 && (
                      <div className="mt-4 rounded-2xl bg-[var(--state-danger-bg)] px-4 py-3">
                        {bucketFlags.map((m, i) => (
                          <p
                            key={i}
                            className="text-[12px] font-medium text-[var(--state-danger)]"
                          >
                            {m}
                          </p>
                        ))}
                      </div>
                    )}

                    <p className="mt-4 text-[12px] text-[var(--text-muted)]">
                      {BUCKET_BLURB[c.bucket] ?? ''}
                    </p>

                    {c.walletTypeCodes?.length > 0 && (
                      <p className="mt-2 text-[11px] text-[var(--text-muted)]">
                        {c.walletTypeCodes.join(' · ')}
                      </p>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* Donut */}
        {donutData.length > 0 && (
          <section className="space-y-4">
            <div className="space-y-1">
              <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                Share of platform balance
              </h2>
              <p className="text-[13px] text-[var(--text-muted)]">
                How the total splits across buckets, by balance.
              </p>
            </div>

            <div className={cn(sectionCard, 'p-6')}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={donutData}
                        cx="50%"
                        cy="50%"
                        innerRadius={70}
                        outerRadius={110}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {donutData.map((d) => (
                          <Cell key={d.bucket} fill={bucketColor(d.bucket)} />
                        ))}
                      </Pie>
                      <Tooltip content={<DonutTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="space-y-2">
                  {donutData.map((d) => (
                    <div
                      key={d.bucket}
                      className="flex items-center justify-between gap-4 text-[13px]"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className="h-3 w-3 shrink-0 rounded-full"
                          style={{ backgroundColor: bucketColor(d.bucket) }}
                        />
                        <span className="truncate text-[var(--text-secondary)]">{d.name}</span>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className="text-[var(--text-muted)]">{d.share}%</span>
                        <span className="font-semibold text-[var(--text-primary)]">
                          {formatCurrency(d.value)}
                        </span>
                      </div>
                    </div>
                  ))}

                  {excludedBuckets.length > 0 && (
                    <p className="pt-3 text-[12px] text-[var(--text-muted)] border-t border-[var(--border-subtle)]">
                      Not drawn (zero or negative balance):{' '}
                      {excludedBuckets
                        .map((c) => `${c.label || humanizeCode(c.bucket)} ${formatCurrency(c.balance)}`)
                        .join(', ')}
                      .
                    </p>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Settled over a range */}
        <section className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="space-y-1">
              <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                Settled in this period
              </h2>
              <p className="text-[13px] text-[var(--text-muted)]">
                What actually left each bucket between these dates — money paid out,
                not money sitting still. The transaction types under each figure are
                what it was summed from.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label className="text-[12px] text-[var(--text-muted)]">
                From
                <input
                  type="date"
                  value={from}
                  max={to}
                  onChange={(e) => setFrom(e.target.value)}
                  className={cn(inputClass, 'ml-2 py-2')}
                />
              </label>
              <label className="text-[12px] text-[var(--text-muted)]">
                To
                <input
                  type="date"
                  value={to}
                  min={from}
                  onChange={(e) => setTo(e.target.value)}
                  className={cn(inputClass, 'ml-2 py-2')}
                />
              </label>
            </div>
          </div>

          {settled.length === 0 ? (
            <div className={cn(sectionCard, 'p-12 text-center text-[14px] text-[var(--text-muted)]')}>
              {isLoading
                ? 'Loading settled figures...'
                : 'Nothing settled in this period.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {settled.map((s, i) => (
                <div
                  key={`${s.bucket}-${i}`}
                  className="rounded-3xl bg-[var(--bg-surface)] p-6 shadow-soft"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="h-3 w-3 shrink-0 rounded-full"
                      style={{ backgroundColor: bucketColor(s.bucket) }}
                    />
                    <span className="text-[13px] font-medium text-[var(--text-muted)]">
                      {s.label || humanizeCode(s.bucket)}
                    </span>
                  </div>

                  <div className="mt-3 font-display text-[26px] font-medium leading-none tracking-[-0.03em] text-[var(--text-primary)]">
                    {formatCurrency(s.amount)}
                  </div>

                  <div className="mt-4 border-t border-[var(--border-subtle)] pt-3 space-y-1">
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                      <Receipt className="h-3.5 w-3.5" strokeWidth={1.5} />
                      Summed from
                    </div>
                    <p className="text-[12px] text-[var(--text-secondary)]">
                      {s.transactionTypes.length > 0
                        ? s.transactionTypes.map((t) => labelTxnType(t)).join(', ')
                        : 'No transaction types reported'}
                      {s.transactionCount !== null && (
                        <span className="text-[var(--text-muted)]">
                          {' '}· {s.transactionCount}{' '}
                          {s.transactionCount === 1 ? 'transaction' : 'transactions'}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Per merchant */}
        <section className="space-y-4">
          <div className="space-y-1">
            <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
              Money per merchant
            </h2>
            <p className="text-[13px] text-[var(--text-muted)]">
              What each merchant sold, what Chekit charged them, and what they are
              holding right now. Open a merchant for their full ledger.
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              setMerchantQ(merchantSearch.trim());
              setMerchantPage(1);
            }}
            className="flex flex-wrap items-center gap-3"
          >
            <div className="relative">
              <Search
                className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-muted)]"
                strokeWidth={1.5}
              />
              <input
                type="text"
                value={merchantSearch}
                onChange={(e) => setMerchantSearch(e.target.value)}
                placeholder="Search merchant name..."
                className={cn(inputClass, 'w-72 pl-10')}
              />
            </div>
            {merchantsFetching && !merchantsLoading && (
              <span className="text-[13px] text-[var(--text-muted)]">Updating...</span>
            )}
          </form>

          <div className={sectionCard}>
            {merchantsLoading && (
              <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
                Loading merchants...
              </div>
            )}

            {!!merchantsError && !merchantsLoading && (
              <div className="p-12 text-center text-[14px] text-[var(--state-danger)]">
                Failed to load per-merchant money.
              </div>
            )}

            {merchants && !merchantsLoading && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[var(--bg-base)]">
                    <tr>
                      <th className={thClass}>Merchant</th>
                      <th className={thClass}>Wallet balance</th>
                      <th className={thClass}>Incoming (earmarked)</th>
                      <th className={thClass}>Stranded</th>
                      <th className={thClass}>Spendable</th>
                      <th className={thClass}>Orders confirmed</th>
                      <th className={thClass}>Gross sold</th>
                      <th className={thClass}>Commission charged</th>
                      <th className={thClass}>Net earned</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-subtle)]">
                    {merchantRows.map((m) => (
                      <tr
                        key={m.businessId}
                        className="transition-colors hover:bg-[var(--bg-base)]"
                      >
                        <td className="px-6 py-4">
                          <Link
                            to={`/finance/owners/business/${m.businessId}`}
                            className="text-[14px] font-medium text-[var(--text-accent)] hover:underline"
                          >
                            {m.businessName ?? 'Unnamed business'}
                          </Link>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="text-[14px] text-[var(--text-primary)]">
                            {formatCurrency(m.walletBalance)}
                          </div>
                          {/* DB value stays auditable, in small print. */}
                          <div className="text-[11px] text-[var(--text-muted)]">
                            {RAW_AVAILABLE_NOTE}: {formatCurrency(m.walletAvailable)}
                          </div>
                        </td>
                        <td
                          className={cn(tdClass, 'text-[var(--text-secondary)]')}
                          title={HELD_INCOMING_NOTE}
                        >
                          {formatCurrency(m.walletHeldIncoming)}
                        </td>
                        <td className={cn(tdClass, strandedClass(m.walletHeldStranded))}>
                          {formatCurrency(m.walletHeldStranded)}
                        </td>
                        <td className={cn(tdClass, 'font-medium')}>
                          {formatCurrency(m.walletSpendable)}
                        </td>
                        <td className={cn(tdClass, 'text-[var(--text-secondary)]')}>
                          {m.ordersConfirmed.toLocaleString()}
                        </td>
                        <td className={tdClass}>{formatCurrency(m.grossSold)}</td>
                        <td className={cn(tdClass, 'text-[var(--text-secondary)]')}>
                          {formatCurrency(m.commissionCharged)}
                        </td>
                        <td className={cn(tdClass, 'font-medium')}>
                          {formatCurrency(m.netEarned)}
                        </td>
                      </tr>
                    ))}

                    {merchantRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-6 py-16 text-center text-[14px] text-[var(--text-muted)]"
                        >
                          {merchantQ
                            ? `No merchant matches "${merchantQ}".`
                            : 'No merchants have moved money yet.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {merchants && merchantTotal > 0 && (
              <div className="flex items-center justify-between gap-4 border-t border-[var(--border-subtle)] px-6 py-4">
                <span className="text-[13px] text-[var(--text-muted)]">
                  Page {merchantPage} of {merchantLastPage} · {merchantTotal} merchants
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setMerchantPage((p) => Math.max(1, p - 1))}
                    disabled={merchantPage <= 1}
                    className="inline-flex items-center gap-1 rounded-full border border-[var(--border-default)] px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setMerchantPage((p) => Math.min(merchantLastPage, p + 1))}
                    disabled={merchantPage >= merchantLastPage}
                    className="inline-flex items-center gap-1 rounded-full border border-[var(--border-default)] px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Drilldown pointer */}
        <div className="flex flex-wrap items-center gap-4 rounded-3xl bg-[var(--bg-surface)] p-6 shadow-soft">
          <p className="flex-1 min-w-[260px] text-[13px] text-[var(--text-muted)]">
            Need one order rather than the whole platform? The split view breaks a
            single order down to the arithmetic — commission on goods, commission on
            delivery, driver pay, and the wallet transactions each one produced.
          </p>
          <Link
            to="/finance/splits"
            className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)]"
          >
            Open split drilldown
            <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </PageGuard>
  );
};
